import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import Box from '@mui/material/Box';
import { Paper, Typography, Stack, LinearProgress } from '@mui/material';

import '../../App.css';
import Header from '../../components/HeaderComponent';
import ProgressControl from '../../components/ProgressControl';
import { Item2 } from '../../styles';
import ErrorDialog from '../../components/ErrorDialog';
import WebSocketConnection from '../Sockets/WebSocketConnection';
import AtomsArray from '../../components/AtomsArray'; // Adjust the path as necessary
import ISpectrumPreviewModel from '../../schemas/Spectrum/ISpectrumPreviewModel';
import MoleculeView from '../../components/MoleculeView'; // Adjust the path as necessary


type PredictCompoundResultProps = {
  location: {
    state: {
      spectrumPreview: ISpectrumPreviewModel
      includedAtoms: [], 
      excludedAtoms: [],
    }
  }
};

const PredictCompoundResult: React.FC<PredictCompoundResultProps> = (props) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isErrorDialogOpened, setErrorDialogOpened] = useState<boolean>(false);
  const [molecules, setMolecules] = useState([]);
  const [progress, setProgress] = useState<number>(0);
  
  const [spectrumPreview] = useState(props.location.state.spectrumPreview);


  const pngString = encodeURIComponent(spectrumPreview?.graph ? spectrumPreview.graph : '');
  const spectrumDataUri = `data:image/png;base64,${pngString}`;

 
  const onError = (errorPacket: any) =>
    showError(errorPacket.message);

  const onAccept = (packet: any) => {
    if (packet.progress !== undefined) {
      setProgress(packet.progress);
    }
    
      console.log('AI prediction completed:', packet);
      setLoading(false);
      const sortedMolecules = [...packet.items].sort((a, b) => {
        // Handle cases where MAE might be undefined or null
        const maeA = a.mae !== undefined && a.mae !== null ? Number(a.mae) : Infinity;
        const maeB = b.mae !== undefined && b.mae !== null ? Number(b.mae) : Infinity;
        return maeA - maeB;
      });
      setMolecules(sortedMolecules);
  }

  useEffect(() => {
    WebSocketConnection.predict('compound', {peaks: spectrumPreview.peaks,
      include: props.location.state.includedAtoms,
      exclude: props.location.state.excludedAtoms, }, 
      onAccept, onError);
  }, []);


  const showError = (message: string) => {
    setErrorMessage(message);
    setErrorDialogOpened(true);
  };

  const onCloseErrorDialog = () => {
    setErrorDialogOpened(false);
  };

  return (
    <Grid container spacing={0} className="main-frame">
      <Grid container md={12}>
        <Item2><div style={{ height: '2em' }}></div></Item2>
      </Grid>
      
      <ErrorDialog 
        isDialogOpened={isErrorDialogOpened} 
        errorMessage={errorMessage} 
        warningMessage={''} 
        onClose={onCloseErrorDialog} 
      />
      
      <Grid xs={12}>
        <Header title="NMR to Structure by AI" helpAddress="help#ai-prediction" showLogin={true} />
      </Grid>

       { isLoading ? <ProgressControl isLoading={isLoading} /> 
          : (
        <>
          {/* First row - Predicted Spectrum */}
          <Grid container xs={12} style={{ marginTop: '2em', display: 'flex', flexDirection: 'column' }}>
           
              <Typography sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                flexGrow: 1
              }} variant="h6" gutterBottom>Spectrum</Typography>
              <Box sx={{ 
                display: 'flex', 
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                flexGrow: 1
              }}>
                {spectrumPreview?.graph ? (
                  <img alt='NMR Spectrum'
                    className='graph-qc'
                    src={spectrumDataUri}
                    style={{ 
                      width: '100%', 
                      maxHeight: '250px', 
                      objectFit: 'contain' 
                    }} 
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '250px',
                    width: '100%'
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      Spectrum visualization not available
                    </Typography>
                  </Box>
                )}
              </Box>

          </Grid>

          <Grid container xs={12} spacing={2} justifyContent="center">
            <Grid xs={12} md={6}>
              <AtomsArray 
                title="Included Atoms"
                readOnly={true}
                selected={props.location.state.includedAtoms || []}
              />  
            </Grid>
            <Grid xs={12} md={6}>
              <AtomsArray 
                title="Excluded Atoms"
                readOnly={true}
                selected={props.location.state.excludedAtoms || []}
              />  
            </Grid>
          </Grid>

          {/* Second row - Molecules Results Table */}
          <Grid container xs={12} spacing={2} justifyContent="center" sx={{ mt: '1em' }}>
            <Grid xs={12}>
              <Paper elevation={0} sx={{ p: 2, width: '100%', boxShadow: 'none', border: 'none' }}>
                <Typography variant="h6" gutterBottom align="center">Predicted Structures</Typography>
                
                {progress < 100 && 
                <Box sx={{ width: '80%', mx: 'auto', mt: 4, mb: '1em' }}>
            <Typography variant="body1" align="center" gutterBottom>
              AI analysis in progress: {progress}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                }
              }}
            />
          </Box>}

                <Grid container spacing={2}>
                  {molecules.map((mol, index) => (
                    <Grid xs={6} md={3} key={index}>
                      <Paper elevation={2} sx={{ 
                        p: 2, 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'flex-end',
                          width: '100%',
                          mb: 1
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            Difference: {mol.mae ? Number(mol.mae).toFixed(2) : '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '150px',
                          width: '100%',
                          flexGrow: 1
                        }}>
                          <MoleculeView 
                            link={''} 
                            moleculeId={undefined}
                            svgContent={mol.svg}
                            isMoleculeInContainer={true}
                            inline={true}
                            moleculeString={mol.molfile}
                          />
                        </Box>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            width: '100%',
                          }}>
                            <Link 
                              to={{
                                pathname: `/compound-search`,
                                state: { 
                                  moleculeString: mol.molfile,
                                  svgContent: mol.svg}
                                }}
                              style={{ 
                                textDecoration: 'none', 
                                color: 'primary.main',
                                marginLeft: '0.5em',
                                marginBottom: '0.5em',
                              }}
                            >
                              Search in OdanChem
                            </Link>
                            <Link 
                              to={{
                                pathname: '/predict-spectrum-result',
                                state: { 
                                  molecule: mol.molfile
                                }
                              }}
                              style={{ 
                                marginBottom: '0.5em',
                                textDecoration: 'none', 
                                color: 'primary.main',
                                marginRight: '0.5em' 
                              }}
                            >
                              Predict 13C NMR
                            </Link>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default PredictCompoundResult;
